import { Edit3, Power, RotateCcw, Users } from 'lucide-react';

import ActionButton from '../../../../components/Buttons/ActionButton';
import SelectField from '../../../../components/Forms/Input/SelectField';

import {
  RestaurantTableDTO,
  RestaurantTableShape,
  RestaurantTableStatus,
} from '../../../../types/restaurant/RestaurantTableTypes';

import TableShapeIcon from './TableShapeIcon';
import TableStatusBadge from './TableStatusBadge';

const shapeLabels: Record<RestaurantTableShape, string> = {
  SQUARE: 'Cuadrada',
  RECTANGLE: 'Rectangular',
  ROUND: 'Redonda',
};

interface TableCardProps {
  table: RestaurantTableDTO;
  onEdit: (table: RestaurantTableDTO) => void;
  onStatusChange: (
    table: RestaurantTableDTO,
    status: RestaurantTableStatus
  ) => void;
  onActiveChange: (table: RestaurantTableDTO, active: boolean) => void;
}

export const tableStatusOptions: {
  value: RestaurantTableStatus;
  label: string;
}[] = [
  { value: 'FREE', label: 'Libre' },
  { value: 'OCCUPIED', label: 'Ocupada' },
  { value: 'RESERVED', label: 'Reservada' },
  { value: 'CLEANING', label: 'En limpieza' },
  { value: 'OUT_OF_SERVICE', label: 'Fuera de servicio' },
];

const TableCard = ({
  table,
  onEdit,
  onStatusChange,
  onActiveChange,
}: TableCardProps) => {
  return (
    <article
      className={`rounded-xl border border-stroke bg-white p-5 transition hover:shadow-card dark:border-strokedark dark:bg-boxdark ${
        table.active ? '' : 'opacity-60'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-bodydark2">
            {table.diningRoomName || 'Sin salón'}
          </p>

          <h3 className="mt-1 font-semibold text-black dark:text-white">
            {table.name}
          </h3>
        </div>

        <TableStatusBadge status={table.status} />
      </div>

      <div className="my-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <TableShapeIcon
            shape={table.shape}
            capacity={table.capacity}
            className="h-16 w-16 shrink-0 text-primary"
          />

          <div>
            <p className="font-mono text-xs font-semibold text-bodydark2">
              {table.code}
            </p>
            <p className="mt-0.5 text-[11px] text-bodydark2">
              {shapeLabels[table.shape]}
            </p>
          </div>
        </div>

        <div className="text-right">
          <div className="inline-flex items-center gap-1.5 text-sm font-medium text-black dark:text-white">
            <Users className="h-4 w-4 text-primary" />
            {table.capacity} personas
          </div>

          <p className="mt-2 text-xs text-bodydark2">
            Orden #{table.displayOrder}
          </p>
        </div>
      </div>

      <SelectField
        label="Cambiar estado"
        name={`tableStatus-${table.id}`}
        value={table.status}
        iconKey="status"
        disabled={!table.active}
        options={tableStatusOptions}
        onChange={(event) =>
          onStatusChange(table, event.target.value as RestaurantTableStatus)
        }
      />

      <div className="mt-1 flex gap-2 border-t border-stroke pt-4 dark:border-strokedark">
        <ActionButton
          label="Editar"
          icon={<Edit3 className="h-4 w-4" />}
          variant="secondary"
          size="md"
          fullWidth
          onClick={() => onEdit(table)}
        />

        <ActionButton
          label={table.active ? 'Desactivar' : 'Reactivar'}
          icon={
            table.active ? (
              <Power className="h-4 w-4" />
            ) : (
              <RotateCcw className="h-4 w-4" />
            )
          }
          variant={table.active ? 'danger' : 'primary'}
          size="md"
          fullWidth
          onClick={() => onActiveChange(table, !table.active)}
        />
      </div>
    </article>
  );
};

export default TableCard;
